/* The Mill Portal — the shell every page lives in.
 *
 * A port of `Mill Portal.dc.html`: a dark rail of sections on the left that
 * folds down to its badges, a breadcrumb bar, and the page beside it. The
 * design embeds each page in an iframe; here the pages are routes, so the
 * rail and the page share one document, one theme and one print job.
 *
 * Two things follow from that and are the only departures:
 *   - the document scrolls rather than an inner frame, with the rail and the
 *     breadcrumb pinned, so charts' hover readouts and printing keep working
 *     exactly as they did before there was a shell;
 *   - "Open full page" opens the same route with `?full=1`, which drops the
 *     rail and the breadcrumb, in place of the design's link to the page's
 *     own file.
 */

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import Attendance from './pages/Attendance'
import Production from './pages/Production'
import Cotton from './pages/Cotton'
import Loads from './pages/Loads'
import Machines from './pages/Machines'
import Profitability from './pages/Profitability'
import Purchases from './pages/Purchases'
import Sales from './pages/Sales'
import SignIn from './pages/SignIn'
import Stock from './pages/Stock'
import Tables from './pages/Tables'
import Trade from './pages/Trade'
import { THEME_LABEL, THEMES, useTheme } from './lib/theme'
import { setUnauthorizedHandler } from './lib/api'
import { AuthContext, ROLE_LABEL, auth, can, loads, useAuth, type User } from './lib/auth'
import { Segmented } from './components/ui'
import { MobileApp } from './mobile/MobileApp'
import { MobileSignIn } from './mobile/MobileSignIn'
import { readSaved as readMobileSaved } from './mobile/model'

const COMPANY = 'Mohans Sri Chand Tex Private Limited'

interface Item {
  id: string
  label: string
  /** Missing on a page that is not built yet — the rail tags it "Soon". */
  to?: string
  title?: string
  element?: ReactNode
  /** A page a viewer has no business on: the rail leaves it out for them. */
  staff?: boolean
  /** Narrower still: admins only. */
  admin?: boolean
}
interface Section { id: string; name: string; abbr: string; items: Item[] }

const NAV: Section[] = [
  { id: 'prod', name: 'Production', abbr: 'PR', items: [
    { id: 'summary', label: 'Production summary', to: '/production',
      title: 'Production Summary', element: <Production /> },
    { id: 'stock', label: 'Stock report', to: '/stock', title: 'Stock Report', element: <Stock /> },
    { id: 'machine', label: 'Machine wise', to: '/machines',
      title: 'Machine Efficiency', element: <Machines /> },
    { id: 'power', label: 'Power' },
    { id: 'electrical', label: 'Electrical' },
    { id: 'maint', label: 'Maintenance' },
  ] },
  // Marketing is split by material, not by source: the Cotton page carries
  // the rates and cotton's own global trade, the Yarn page the yarn prices
  // and yarn's.
  { id: 'mkt', name: 'Marketing', abbr: 'MK', items: [
    { id: 'cotton', label: 'Cotton', to: '/cotton', title: 'Cotton', element: <Cotton /> },
    { id: 'trade', label: 'Yarn', to: '/trade', title: 'Yarn', element: <Trade /> },
  ] },
  { id: 'proc', name: 'Procurement', abbr: 'PC', items: [
    { id: 'purchases', label: 'Cotton purchases', to: '/purchases',
      title: 'Cotton Purchases', element: <Purchases /> },
  ] },
  { id: 'sales', name: 'Sales', abbr: 'SL', items: [
    { id: 'salesdisp', label: 'Yarn sales', to: '/sales', title: 'Yarn Sales', element: <Sales /> },
    // The same board on the waste register; keyed so moving between the
    // two remounts it rather than carrying one register's pickers to the other.
    { id: 'waste', label: 'Waste sales', to: '/waste-sales', title: 'Waste Sales',
      element: <Sales key="waste" product="waste" /> },
  ] },
  // One workforce page shown twice: who is on the floor against the
  // requirement, and who turned up day by day.
  { id: 'hr', name: 'HR', abbr: 'HR', items: [
    { id: 'staffing', label: 'Staffing', to: '/staffing',
      title: 'Staffing', element: <Attendance view="staffing" /> },
    { id: 'attendance', label: 'Attendance', to: '/attendance',
      title: 'Attendance', element: <Attendance view="attendance" /> },
  ] },
  { id: 'fin', name: 'Finance', abbr: 'FN', items: [
    { id: 'finance', label: 'Finance overview' },
  ] },
  { id: 'calc', name: 'Calculators', abbr: 'CA', items: [
    { id: 'profit', label: 'Profitability', to: '/profitability',
      title: 'Profitability', element: <Profitability /> },
    { id: 'calculators', label: 'Calculators' },
  ] },
  // Staff only: where files come in and where the tables can be read raw.
  { id: 'data', name: 'Data', abbr: 'DA', items: [
    { id: 'loads', label: 'Data loads', to: '/loads', title: 'Data loads', element: <Loads />, staff: true },
    { id: 'tables', label: 'Data tables', to: '/tables', title: 'Data tables', element: <Tables />, staff: true, admin: true },
  ] },
]

const pathOf = (it: Item) => it.to ?? `/soon/${it.id}`

/** Which section and item a path belongs to; the first page if none. */
function locate(pathname: string): { section: Section; item: Item } {
  for (const section of NAV) {
    for (const item of section.items) {
      if (pathname === pathOf(item) || pathname.startsWith(pathOf(item) + '/')) {
        return { section, item }
      }
    }
  }
  return { section: NAV[0], item: NAV[0].items[0] }
}

// ------------------------------------------------------------ install
/** Chrome and Edge raise `beforeinstallprompt` once the site qualifies as
 *  an app (manifest, icons, HTTPS). Holding on to the event lets the rail
 *  offer "Install app" in the app's own words instead of leaving people to
 *  find the icon in the address bar. Safari never raises it; there the
 *  route is File → Add to Dock, and the button stays hidden. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function useInstallPrompt() {
  const [prompt, setPrompt] = useState<InstallPromptEvent | null>(null)
  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches
    if (standalone) return
    const keep = (e: Event) => { e.preventDefault(); setPrompt(e as InstallPromptEvent) }
    const done = () => setPrompt(null)
    window.addEventListener('beforeinstallprompt', keep)
    window.addEventListener('appinstalled', done)
    return () => {
      window.removeEventListener('beforeinstallprompt', keep)
      window.removeEventListener('appinstalled', done)
    }
  }, [])
  const install = async () => {
    if (!prompt) return
    await prompt.prompt()
    const { outcome } = await prompt.userChoice
    if (outcome === 'accepted') setPrompt(null)
  }
  return prompt ? install : null
}

// ------------------------------------------------------- remembered state
const KEY = 'millPortal'
interface Saved { open: boolean; expanded: Record<string, boolean> }

function readSaved(current: Section): Saved {
  let saved: Partial<Saved> = {}
  try { saved = JSON.parse(window.localStorage.getItem(KEY) || '{}') } catch { /* unreadable */ }
  const narrow = window.innerWidth < 760
  return {
    open: narrow ? false : saved.open !== false,
    // First visit: the section being looked at is the one that is open.
    expanded: saved.expanded ?? { [current.id]: true },
  }
}

// ----------------------------------------------------------------- session
/** The gate: ask the server who this is, then show the sign-in page or the
 *  portal. `undefined` is "not yet known"; `null` is "nobody".
 *
 *  Any API call that comes back 401 after sign-in drops the user straight
 *  back here, through the handler registered with the client — so an
 *  expired session is one sign-in page, not an error box on every panel. */
export default function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined)
  const [pending, setPending] = useState(0)
  const phone = useIsPhoneRoute()

  useEffect(() => {
    let alive = true
    auth.me().then(
      (u) => { if (alive) setUser(u) },
      () => { if (alive) setUser(null) },
    )
    return () => { alive = false }
  }, [])

  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null))
    return () => setUnauthorizedHandler(null)
  }, [])

  // The pending count on the rail: fetched once a reviewer or editor is in,
  // and kept current by the Data loads page itself after that.
  useEffect(() => {
    if (!user || !can(user, 'load')) { setPending(0); return }
    let alive = true
    loads.list().then((r) => { if (alive) setPending(r.pending) }, () => { /* the page will say */ })
    return () => { alive = false }
  }, [user])

  const signOut = async () => {
    try { await auth.logout() } catch { /* the cookie may already be gone */ }
    setUser(null)
  }

  if (user === undefined) {
    return (
      <div style={{
        minHeight: '100vh', display: 'grid', placeItems: 'center',
        background: 'var(--rail-bg)', color: 'var(--rail-fg)', fontSize: 14,
      }}>Loading…</div>
    )
  }
  if (user === null) return phone ? <MobileSignIn onSignedIn={setUser} /> : <SignIn onSignedIn={setUser} />

  return (
    <AuthContext.Provider value={{ user, signOut, pending, setPending }}>
      {phone ? <MobileApp /> : <Portal />}
    </AuthContext.Provider>
  )
}

// -------------------------------------------------------------- the phone
/** Which app this is: the phone one under /m, or the desktop portal.
 *
 *  The portal's rail, breadcrumb and wide tables do not survive a 390px
 *  column, so a phone gets the mobile app instead — see mobile/MobileApp.
 *  Two ways in: the /m routes are explicit and work at any width (which is
 *  how the mobile app is developed and reviewed on a desktop), and a narrow
 *  viewport at any other route is sent there once. "Full web app" in the
 *  mobile drawer sets a flag that stops the redirect, so a phone can still
 *  reach the pages the mobile app leaves out. */
const PHONE = '(max-width: 719px)'

function useIsPhoneRoute(): boolean {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const onMobileRoute = pathname === '/m' || pathname.startsWith('/m/')

  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(PHONE).matches,
  )
  useEffect(() => {
    const mq = window.matchMedia(PHONE)
    const sync = () => setNarrow(mq.matches)
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  // Only on arrival, and only if they have not asked for the full app:
  // redirecting on every render would trap someone who followed a link to a
  // desktop page on purpose.
  const sent = useRef(false)
  useEffect(() => {
    if (onMobileRoute || sent.current || !narrow || readMobileSaved().desktop) return
    sent.current = true
    navigate('/m', { replace: true })
  }, [narrow, onMobileRoute, navigate])

  return onMobileRoute
}

// ------------------------------------------------------------------ portal
function Portal() {
  const { user, signOut, pending } = useAuth()
  const install = useInstallPrompt()
  const { pathname, search } = useLocation()
  const { section, item } = locate(pathname)
  const full = new URLSearchParams(search).has('full')
  const [theme, setTheme] = useTheme()
  const [state, setState] = useState<Saved>(() => readSaved(section))
  const { open, expanded } = state
  const staff = can(user, 'load')
  const manage = can(user, 'manage')

  const set = (patch: Partial<Saved>) => {
    const next = { ...state, ...patch }
    setState(next)
    try { window.localStorage.setItem(KEY, JSON.stringify(next)) } catch { /* private mode */ }
  }

  const built = Boolean(item.element)

  return (
    <div className="portal">
      {full ? null : (
        <aside className={`portal-rail no-print ${open ? '' : 'closed'}`}>
          <div className="portal-rail-head">
            {open ? (
              <div style={{ minWidth: 0 }}>
                <div className="portal-rail-eyebrow">Mohans Sri Chand Tex</div>
                <div className="portal-rail-title">Mill dashboards</div>
              </div>
            ) : null}
            <button type="button" className="portal-toggle" title="Collapse / expand"
              onClick={() => set({ open: !open })}>{open ? '‹' : '›'}</button>
          </div>

          <nav className="portal-nav">
            {NAV.map((s) => {
              const active = s === section
              const shown = open && Boolean(expanded[s.id])
              const items = s.items.filter((it) => (staff || !it.staff) && (manage || !it.admin))
              // A section with nothing a viewer may open (Data) is not shown to them.
              if (items.length === 0) return null
              return (
                <div key={s.id} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <button type="button" className="portal-section" title={s.name}
                    onClick={() => (open
                      ? set({ expanded: { ...expanded, [s.id]: !expanded[s.id] } })
                      : set({ open: true, expanded: { ...expanded, [s.id]: true } }))}>
                    <span className={`portal-badge ${active ? 'on' : ''}`}>{s.abbr}</span>
                    {open ? (
                      <>
                        <span className="portal-section-name">{s.name}</span>
                        <span style={{ fontSize: 10, opacity: 0.6 }}>{expanded[s.id] ? '▾' : '▸'}</span>
                      </>
                    ) : null}
                  </button>
                  {shown ? (
                    <div className="portal-items">
                      {items.map((it) => (
                        <Link key={it.id} to={pathOf(it)}
                          className={`portal-item ${it === item ? 'on' : ''}`}>
                          <span>{it.label}</span>
                          {it.element ? null : <span className="portal-soon">Soon</span>}
                          {it.id === 'loads' && pending > 0 ? (
                            <span className="portal-soon" title={`${pending} load${pending === 1 ? '' : 's'} awaiting review`}>
                              {pending} pending
                            </span>
                          ) : null}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </nav>

          {/* Who is signed in. Folded, just the initial; the name and role
              ride on its tooltip. */}
          {/* Offered only while the browser can install it; gone once it is. */}
          {install && open ? (
            <div className="no-print" style={{ padding: '0 14px 8px' }}>
              <button type="button" className="btn btn-ghost" title="Add Millstack to this computer as an app"
                style={{ fontSize: 12, padding: '5px 10px', width: '100%' }}
                onClick={install}>Install app</button>
            </div>
          ) : null}
          <div className="portal-rail-foot" title={open ? undefined : `${user.username} · ${ROLE_LABEL[user.role]}`}>
            <span className="portal-badge" aria-hidden="true">{user.username.slice(0, 1).toUpperCase()}</span>
            {open ? (
              <>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="truncate" style={{ fontSize: 13, fontWeight: 600 }}>{user.username}</div>
                  <div style={{ fontSize: 11, opacity: 0.65 }}>{ROLE_LABEL[user.role]}</div>
                </div>
                <button type="button" className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 10px' }}
                  onClick={signOut}>Sign out</button>
              </>
            ) : null}
          </div>
        </aside>
      )}

      <div className="portal-main">
        {full ? null : (
          <div className="portal-crumb no-print">
            <span style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>{section.name}</span>
            <span>/</span>
            <span style={{ color: 'var(--color-text)', fontWeight: 600 }}>{item.label}</span>
            {built ? (
              <a href={`${pathOf(item)}?full=1`} target="_blank" rel="noreferrer"
                style={{ marginLeft: 'auto', fontSize: 12 }}>Open full page ↗</a>
            ) : null}
          </div>
        )}

        {built ? (
          <div style={{ minHeight: '100vh', background: 'var(--color-bg)', paddingBottom: 64 }}>
            <header style={{
              display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
              gap: 24, flexWrap: 'wrap', padding: '26px 32px 18px',
              borderBottom: '1px solid var(--color-divider)',
            }}>
              <div>
                <div style={{
                  fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
                  color: 'var(--color-accent-700)',
                }}>{COMPANY}</div>
                <h1 style={{
                  fontSize: 40, lineHeight: 1, margin: '6px 0 0', letterSpacing: '-0.01em',
                }}>{item.title}</h1>
                <div id="subline" style={{
                  fontSize: 14, color: 'var(--color-neutral-700)', marginTop: 6, maxWidth: '96ch',
                }} />
              </div>

              <nav className="no-print" style={{
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              }}>
                <button className="btn btn-secondary" type="button" onClick={() => window.print()}>
                  Print / PDF
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 4 }}>
                  <span className="axis-title">Theme</span>
                  <Segmented
                    value={theme}
                    onChange={setTheme}
                    options={THEMES.map((t) => ({ value: t, label: THEME_LABEL[t] }))}
                  />
                </div>
              </nav>
            </header>

            <Routes>
              {NAV.flatMap((s) => s.items).filter((it) => it.element).map((it) => (
                <Route key={it.id} path={it.to} element={it.element} />
              ))}
              <Route path="*" element={<Navigate to="/production" replace />} />
            </Routes>
          </div>
        ) : (
          <Soon section={section.name} page={item.label} />
        )}
      </div>
    </div>
  )
}

/** A page the rail lists but nobody has built: the design's own holding text. */
function Soon({ section, page }: { section: string; page: string }) {
  return (
    <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 40, minHeight: '70vh' }}>
      <div style={{ maxWidth: 440, textAlign: 'center' }}>
        <div style={{
          fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
          color: 'var(--color-accent-700)',
        }}>{section}</div>
        <h1 style={{ fontSize: 40, lineHeight: 1, margin: '8px 0 12px' }}>{page}</h1>
        <p className="pretty" style={{
          fontSize: 14, lineHeight: 1.55, color: 'var(--color-neutral-700)', margin: 0,
        }}>
          This page isn't built yet. Share the source report or data and it will appear here.
        </p>
      </div>
    </div>
  )
}

/** The header subline is per-page, so pages portal their own text into it
 *  rather than the shell guessing what each one wants to say.
 *
 *  The target is looked up in an effect, not during render: on the first
 *  pass the header has not been committed yet, so `getElementById` would
 *  return null and the subline would silently never appear. */
export function Subline({ children }: { children: ReactNode }) {
  const [host, setHost] = useState<HTMLElement | null>(null)
  useEffect(() => { setHost(document.getElementById('subline')) }, [])
  return host ? createPortal(children, host) : null
}

export const MAIN_STYLE: CSSProperties = {
  maxWidth: 1400,
  margin: '0 auto',
  padding: '24px 32px',
  display: 'flex',
  flexDirection: 'column',
  gap: 28,
}
